import { motion, AnimatePresence } from 'framer-motion'

/**
 * Variantes de animación para el fondo oscurecido (backdrop).
 * @type {import('framer-motion').Variants}
 */
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 0.5 },
  exit: { opacity: 0 },
}

/**
 * Variantes de animación para la ventana modal.
 * Maneja la opacidad, posición vertical y escala.
 * @type {import('framer-motion').Variants}
 */
const modalVariants = {
  hidden: { opacity: 0, y: -50, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, y: -50, scale: 0.95, transition: { duration: 0.2 } },
}

/**
 * Componente de Ventana Modal Animada.
 * * Utiliza `framer-motion` para transiciones fluidas de entrada y salida.
 * Se integra con las clases de Bootstrap para el estilo visual.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.isOpen - Determina si el modal es visible.
 * @param {function} props.onClose - Función de callback para cerrar el modal.
 * @param {string} props.title - Título que aparece en la cabecera.
 * @param {React.ReactNode} props.children - Contenido que se renderiza en el cuerpo del modal.
 * @param {boolean} [props.isLarge=false] - Si es true, aplica la clase `modal-lg` para mayor ancho.
 * @returns {JSX.Element} El componente Modal envuelto en AnimatePresence.
 */
const Modal = ({ isOpen, onClose, title, children, isLarge = false }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="modal-backdrop fade show"
            style={{ zIndex: 9998 }}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="modal d-block fade show"
            tabIndex="-1"
            role="dialog"
            style={{ zIndex: 9999 }}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div
              className={`modal-dialog modal-dialog-centered ${isLarge ? 'modal-lg' : ''}`}
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header header">
                  <h5 className="modal-title">{title}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={onClose}
                  />
                </div>

                <div className="modal-body">{children}</div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default Modal
